// PaymentMethodBreakdown.js
// Compact drop-down under a Cash Flow summary card: splits money in (or out) by
// payment method. Picking a row filters the table to that method.
import React from 'react';
import { Menu, MenuItem, Box, Typography, Divider } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

const METHOD_COLORS = {
  cash: '#2e7d32',
  check: '#ed6c02',
  credit_card: '#1565c0',
  bank_transfer: '#7b1fa2',
  other: '#546e7a',
  unknown: '#9e9e9e',
};

const formatILS = (value) => new Intl.NumberFormat('he-IL', {
  style: 'currency',
  currency: 'ILS',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
}).format(value || 0);

const formatPercent = (value) => `${value < 1 && value > 0 ? '<1' : Math.round(value)}%`;

function PaymentMethodBreakdown({ anchorEl, open, onClose, rows = [], selectedMethod = null, onSelect }) {
  const total = rows.reduce((sum, r) => sum + (r.amount || 0), 0);

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      slotProps={{ paper: { sx: { minWidth: 220, maxWidth: 'calc(100vw - 16px)' } } }}
      MenuListProps={{ dense: true }}
    >
      {rows.length === 0 && (
        <MenuItem disabled>
          <Typography variant="body2">אין תנועות בטווח</Typography>
        </MenuItem>
      )}
      {rows.map((r) => {
        const selected = selectedMethod === r.method;
        return (
          <MenuItem key={r.method} selected={selected} onClick={() => onSelect(selected ? null : r)}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              {selected ? (
                <CheckIcon sx={{ fontSize: 14, color: METHOD_COLORS[r.method] || METHOD_COLORS.other }} />
              ) : (
                <Box sx={{ width: 8, height: 8, mx: '3px', borderRadius: '50%', flexShrink: 0, bgcolor: METHOD_COLORS[r.method] || METHOD_COLORS.other }} />
              )}
              <Typography variant="body2" sx={{ flex: 1, fontWeight: selected ? 600 : 400 }}>{r.label}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{formatILS(r.amount)}</Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                dir="ltr"
                sx={{ width: 34, textAlign: 'left', flexShrink: 0 }}
              >
                {formatPercent(total > 0 ? (r.amount / total) * 100 : 0)}
              </Typography>
            </Box>
          </MenuItem>
        );
      })}
      {selectedMethod && <Divider />}
      {selectedMethod && (
        <MenuItem onClick={() => onSelect(null)}>
          <Typography variant="body2" color="primary">הצג הכל</Typography>
        </MenuItem>
      )}
    </Menu>
  );
}

export default PaymentMethodBreakdown;
